import { Component, ViewChild } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap';
import { forkJoin } from 'rxjs';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { CustomField, CustomFieldValue } from '../../../models/custom-field.model';
import { HCPService } from '../hcp.service';

import { SettingService } from '../../../settings/components/settings/setting.service';

interface DiffRow {
  label: string;
  oldValue: string;
  newValue: string;
  changed: boolean;
}

@Component({
  selector: 'crm-hcp-edit-request-diff',
  templateUrl: './edit-request-diff.component.html',
  styleUrls: ['./edit-request-diff.component.css']
})
export class HcpEditRequestDiffComponent {
  @ViewChild('diffModal') diffModal: ModalDirective;
  rows: DiffRow[] = [];
  hasOriginal = true;

  private fieldDefs = [
    { field: 'fullName', label: 'Name' },
    { field: 'englishName', label: 'English Name' },
    { field: 'phone', label: 'Phone' },
    { field: 'email', label: 'Email' },
    { field: 'specialityName', label: 'Speciality' },
    { field: 'active', label: 'Active' },
    { field: 'goldenList', label: 'Golden List' }
  ];

  constructor(private readonly hcpService: HCPService,
    private readonly customFieldsService: CustomFieldsService,
    private readonly settingService: SettingService) {
    // English Name follows the HCPCostumFieldLabel setting: its label, and no row while the setting is off.
    this.settingService.getCustomFieldLabelConfig('HCPCostumFieldLabel').subscribe(config => {
      const def = this.fieldDefs.find(d => d.field === 'englishName');
      if (!def) return;
      if (config.show) def.label = config.label;
      else this.fieldDefs.splice(this.fieldDefs.indexOf(def), 1);
    });
  }

  show(editRequestId: number) {
    forkJoin(this.hcpService.getHcpEditRequestComparison(editRequestId), this.customFieldsService.getActive('hcp')).subscribe(([response, customFields]: [any, CustomField[]]) => {
      const original = response.original;
      const proposed = response.proposed;
      this.hasOriginal = !!original;
      const allRows = this.fieldDefs.map(def => {
        const oldValue = original ? original[def.field] : null;
        const newValue = proposed ? proposed[def.field] : null;
        return {
          label: def.label,
          oldValue: this.displayValue(oldValue),
          newValue: this.displayValue(newValue),
          changed: this.hasOriginal && this.normalize(oldValue) !== this.normalize(newValue)
        };
      });
      // Admin-defined custom fields: one row per active field, values resolved by the API.
      for (const field of customFields || []) {
        const oldValue = this.customDisplay(original, field.id);
        const newValue = this.customDisplay(proposed, field.id);
        allRows.push({
          label: field.name,
          oldValue: this.displayValue(oldValue),
          newValue: this.displayValue(newValue),
          changed: this.hasOriginal && this.normalize(oldValue) !== this.normalize(newValue)
        });
      }
      // When we have a real original to compare against, only the fields that actually
      // changed are worth showing. Without one there's nothing to filter by, so fall back
      // to showing every proposed value.
      this.rows = this.hasOriginal ? allRows.filter(r => r.changed) : allRows;
      this.diffModal.show();
    });
  }

  hide() {
    this.diffModal.hide();
  }

  private customDisplay(dto: any, fieldId: number): string {
    const values: CustomFieldValue[] = dto && dto.customFields;
    const value = values && values.find(v => v.fieldId === fieldId);
    return value ? value.displayValue : null;
  }

  private normalize(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  private displayValue(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }
}
